import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import ProductContainer from "../AppPAges/Dashboard/Invoice/ProductConatiner";
import "./pos.styles.css";
import removedButton from "../../../assets/Image/removebutton.png";
import useApi from "../../../utils/api-manager/Helper/useApi";
import { runCloverLocalSale } from "../../../utils/clover/cloverConnector";
import AuthContext from "../../../utils/secure-route/AuthContext";
import { ToastContainer } from "react-toastify";
import ReactCardFlip from "react-card-flip";
import { useToastHandler } from "../../common/ToastHandler";
import useFormattingUtils from "../../../context/useFormattingUtils";
import { ShoppingCart } from 'lucide-react';
import ProductModal from "./components/ProductModal";
import ProductList from "./components/ProductList";
import DeleteIcon from "../../../assets/Image/remove.png";
import TransactionTable from "./components/TransactionTable";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

const STORAGE_KEY = 'selectedProducts';
const POS_LAYOUT_PREFS_KEY = 'posLayoutPreferences';
const POS_PRODUCT_META_KEY = 'posProductLocalMeta';
const POS_LOCK_PASSWORD_KEY = 'posFullscreenLockPassword';
const POS_LOCK_ENABLED_KEY = 'posFullscreenLockEnabled';
const POS_LOCK_SKIPPED_KEY = 'posFullscreenLockSkipped';
const POS_LOCK_ACTIVE_KEY = 'posFullscreenLockActive';

const DEFAULT_POS_LAYOUT_PREFS = {
  panelPosition: 'left',
  cardSize: 'medium',
  groupBySection: false,
};

const CARD_SIZE_CONFIG = {
  small: { min: 104, row: 78, label: 'Small' },
  medium: { min: 132, row: 96, label: 'Medium' },
  large: { min: 168, row: 124, label: 'Large' },
};

const loadJsonFromStorage = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const createCartItemId = (product) => {
  const productId = product?.id || product?.index || product?.title || 'item';
  return `${productId}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

const formatReceiptAmount = (value) => {
  const number = parseFloat(value);
  return Number.isNaN(number) ? "0.00" : number.toFixed(2);
};

const ThermalReceiptModal = ({ open, onClose, data, user }) => {
  if (!open || !data) return null;

  const receiptItems = Array.isArray(data.items) ? data.items : [];
  const invoiceNo = data?.invoice?.invoice_no || data?.invoice_no || "N/A";
  const invoiceDate = data?.invoice?.date || data?.date || new Date().toISOString().split("T")[0];
  const totalQty = data?.invoice?.total_qty || receiptItems.reduce((sum, item) => sum + (parseInt(item.qty, 10) || 0), 0);
  const totalAmount = data?.invoice?.total_amount || data?.total_amount || receiptItems.reduce((sum, item) => sum + (parseFloat(item.total_amount) || 0), 0);
  const paymentMethod = data?.invoice?.pay_method || data?.pay_method || data?.payment_method || "Cash / POS";

  const handlePrintReceipt = () => {
    window.print();
  };

  return (
    <div className="thermal-receipt-overlay">
      <style>{`
        .thermal-receipt-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.62);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
        }
        .thermal-receipt-modal {
          width: min(94vw, 420px);
          max-height: 94vh;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .thermal-receipt-paper {
          background: #fff;
          color: #111827;
          width: 320px;
          max-width: 100%;
          margin: 0 auto;
          padding: 18px 14px 16px;
          border-radius: 4px;
          box-shadow: 0 18px 45px rgba(0, 0, 0, 0.35);
          font-family: "Courier New", Courier, monospace;
          position: relative;
          overflow: hidden;
        }
        .thermal-receipt-paper::before,
        .thermal-receipt-paper::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          height: 8px;
          background: repeating-linear-gradient(90deg, #ffffff 0 10px, transparent 10px 20px);
        }
        .thermal-receipt-paper::before { top: -4px; }
        .thermal-receipt-paper::after { bottom: -4px; }
        .receipt-store-name {
          text-align: center;
          font-size: 18px;
          font-weight: 800;
          line-height: 1.15;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .receipt-store-info {
          text-align: center;
          font-size: 11px;
          line-height: 1.35;
          color: #374151;
          word-break: break-word;
        }
        .receipt-title {
          text-align: center;
          font-size: 13px;
          font-weight: 800;
          margin: 10px 0 8px;
          letter-spacing: 1px;
        }
        .receipt-divider {
          border-top: 1px dashed #111827;
          margin: 8px 0;
        }
        .receipt-meta-row,
        .receipt-total-row,
        .receipt-item-grid,
        .receipt-item-header {
          display: grid;
          grid-template-columns: 1fr auto;
          column-gap: 8px;
          align-items: start;
        }
        .receipt-meta-row {
          font-size: 11px;
          line-height: 1.5;
        }
        .receipt-item-header {
          grid-template-columns: 1fr 36px 54px 58px;
          font-size: 10px;
          font-weight: 800;
          padding-bottom: 4px;
        }
        .receipt-item-grid {
          grid-template-columns: 1fr 36px 54px 58px;
          font-size: 11px;
          line-height: 1.35;
          padding: 5px 0;
        }
        .receipt-item-name {
          font-weight: 700;
          padding-right: 4px;
          word-break: break-word;
        }
        .receipt-align-right { text-align: right; }
        .receipt-summary {
          margin-top: 6px;
        }
        .receipt-total-row {
          font-size: 12px;
          line-height: 1.55;
        }
        .receipt-grand-total {
          font-size: 16px;
          font-weight: 900;
          padding-top: 5px;
        }
        .receipt-footer {
          text-align: center;
          font-size: 11px;
          line-height: 1.45;
          margin-top: 10px;
          color: #374151;
        }
        .thermal-receipt-actions {
          display: flex;
          justify-content: center;
          gap: 10px;
        }
        .thermal-receipt-actions button {
          border: none;
          border-radius: 8px;
          padding: 10px 18px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
        }
        .receipt-print-button {
          background: #111827;
          color: #fff;
        }
        .receipt-close-button {
          background: #e5e7eb;
          color: #111827;
        }
        @media print {
          body * { visibility: hidden !important; }
          .thermal-receipt-overlay,
          .thermal-receipt-overlay * { visibility: visible !important; }
          .thermal-receipt-overlay {
            position: fixed;
            inset: 0;
            background: #fff !important;
            padding: 0;
            align-items: flex-start;
            justify-content: center;
          }
          .thermal-receipt-modal {
            width: 80mm;
            max-height: none;
          }
          .thermal-receipt-paper {
            width: 72mm;
            box-shadow: none;
            border-radius: 0;
            padding: 4mm 3mm;
          }
          .thermal-receipt-actions { display: none !important; }
          @page { size: 80mm auto; margin: 0; }
        }
      `}</style>

      <div className="thermal-receipt-modal">
        <div className="thermal-receipt-paper">
          <div className="receipt-store-name">{user?.store_name || "Store Name"}</div>
          <div className="receipt-store-info">{user?.store_address || "Store Address"}</div>
          <div className="receipt-store-info">{user?.store_phone ? `Mob: ${user.store_phone}` : ""}</div>
          <div className="receipt-title">TAX INVOICE / BILL</div>

          <div className="receipt-divider" />
          <div className="receipt-meta-row"><span>Invoice No</span><strong>{invoiceNo}</strong></div>
          <div className="receipt-meta-row"><span>Date</span><strong>{invoiceDate}</strong></div>
          <div className="receipt-meta-row"><span>Payment</span><strong>{paymentMethod}</strong></div>
          <div className="receipt-divider" />

          <div className="receipt-item-header">
            <span>ITEM</span>
            <span className="receipt-align-right">QTY</span>
            <span className="receipt-align-right">RATE</span>
            <span className="receipt-align-right">AMT</span>
          </div>
          <div className="receipt-divider" />

          {receiptItems.map((item, index) => (
            <div className="receipt-item-grid" key={`${item.pos_item_title}-${index}`}>
              <span className="receipt-item-name">{item.pos_item_title || "Item"}</span>
              <span className="receipt-align-right">{item.qty || 0}</span>
              <span className="receipt-align-right">{formatReceiptAmount(item.amount_per_item)}</span>
              <span className="receipt-align-right">{formatReceiptAmount(item.total_amount)}</span>
            </div>
          ))}

          <div className="receipt-divider" />
          <div className="receipt-summary">
            <div className="receipt-total-row"><span>Total Qty</span><strong>{totalQty}</strong></div>
            <div className="receipt-total-row"><span>Subtotal</span><strong>₹{formatReceiptAmount(totalAmount)}</strong></div>
            <div className="receipt-total-row"><span>Round Off</span><strong>₹0.00</strong></div>
            <div className="receipt-divider" />
            <div className="receipt-total-row receipt-grand-total"><span>TOTAL</span><span>₹{formatReceiptAmount(totalAmount)}</span></div>
          </div>

          <div className="receipt-divider" />
          <div className="receipt-footer">
            Thank you for your purchase!<br />
            Please visit again.<br />
            *** Computer Generated Bill ***
          </div>
        </div>

        <div className="thermal-receipt-actions">
          <button type="button" className="receipt-print-button" onClick={handlePrintReceipt}>Print Bill</button>
          <button type="button" className="receipt-close-button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};


const FullscreenLockSetupModal = ({
  open,
  password,
  confirmPassword,
  error,
  onPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onSkip,
}) => {
  if (!open) return null;

  return (
    <div className="pos-lock-overlay" onClick={(e) => e.stopPropagation()}>
      <style>{`
        .pos-lock-overlay {
          position: fixed;
          inset: 0;
          z-index: 2147483647;
          background: rgba(15, 23, 42, 0.92);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          backdrop-filter: blur(12px);
          pointer-events: auto;
          user-select: none;
        }
        .pos-lock-card {
          width: min(94vw, 460px);
          background: #ffffff;
          border-radius: 22px;
          box-shadow: 0 30px 80px rgba(2, 6, 23, 0.38);
          padding: 24px;
          border: 1px solid rgba(226, 232, 240, 0.9);
        }
        .pos-lock-badge {
          display: inline-flex;
          background: #ecfeff;
          color: #0e7490;
          border: 1px solid #a5f3fc;
          border-radius: 999px;
          padding: 7px 12px;
          font-size: 12px;
          font-weight: 900;
          margin-bottom: 14px;
        }
        .pos-lock-title {
          margin: 0;
          color: #0f172a;
          font-size: 24px;
          font-weight: 950;
          letter-spacing: -0.04em;
        }
        .pos-lock-text {
          color: #64748b;
          margin: 8px 0 18px;
          font-size: 13px;
          line-height: 1.6;
          font-weight: 700;
        }
        .pos-lock-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 12px;
        }
        .pos-lock-field label {
          color: #334155;
          font-size: 12px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .pos-lock-field input {
          height: 42px;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          padding: 0 12px;
          font-size: 14px;
          font-weight: 800;
          outline: none;
        }
        .pos-lock-field input:focus {
          border-color: #0891b2;
          box-shadow: 0 0 0 4px rgba(8, 145, 178, 0.12);
        }
        .pos-lock-error {
          background: #fef2f2;
          color: #b91c1c;
          border: 1px solid #fecaca;
          padding: 10px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 12px;
        }
        .pos-lock-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          flex-wrap: wrap;
          margin-top: 16px;
        }
        .pos-lock-primary,
        .pos-lock-secondary,
        .pos-lock-danger {
          border: none;
          border-radius: 999px;
          padding: 10px 16px;
          font-size: 13px;
          font-weight: 950;
          cursor: pointer;
        }
        .pos-lock-primary {
          background: #0f172a;
          color: #ffffff;
        }
        .pos-lock-secondary {
          background: #f1f5f9;
          color: #334155;
        }
        .pos-lock-danger {
          background: #dc2626;
          color: #ffffff;
        }
        @media (max-width: 520px) {
          .pos-lock-actions {
            flex-direction: column-reverse;
          }
          .pos-lock-primary,
          .pos-lock-secondary,
          .pos-lock-danger {
            width: 100%;
          }
        }
      `}</style>

      <form className="pos-lock-card" onSubmit={onSubmit} onClick={(e) => e.stopPropagation()}>
        <div className="pos-lock-badge">Fullscreen Security</div>
        <h2 className="pos-lock-title">Lock this POS session?</h2>
        <p className="pos-lock-text">
          Create a temporary session password. When fullscreen is exited or minimized, the POS screen will be locked until this password is entered again.
        </p>

        {error && <div className="pos-lock-error">{error}</div>}

        <div className="pos-lock-field">
          <label>Session Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder="Enter password"
            autoFocus
          />
        </div>

        <div className="pos-lock-field">
          <label>Confirm Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
            placeholder="Confirm password"
          />
        </div>

        <div className="pos-lock-actions">
          <button type="button" className="pos-lock-secondary" onClick={onSkip}>
            Use Without Lock
          </button>
          <button type="submit" className="pos-lock-primary">
            Enable Lock
          </button>
        </div>
      </form>
    </div>
  );
};

const FullscreenLockedOverlay = ({
  open,
  password,
  error,
  onPasswordChange,
  onUnlock,
  onLogout,
}) => {
  if (!open) return null;

  return (
    <div className="pos-lock-overlay" onClick={(e) => e.stopPropagation()}>
      <style>{`
        .pos-lock-overlay {
          position: fixed;
          inset: 0;
          z-index: 2147483647;
          background: rgba(15, 23, 42, 0.92);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          backdrop-filter: blur(12px);
          pointer-events: auto;
          user-select: none;
        }
        .pos-lock-card {
          width: min(94vw, 460px);
          background: #ffffff;
          border-radius: 22px;
          box-shadow: 0 30px 80px rgba(2, 6, 23, 0.38);
          padding: 24px;
          border: 1px solid rgba(226, 232, 240, 0.9);
        }
        .pos-lock-badge {
          display: inline-flex;
          background: #ecfeff;
          color: #0e7490;
          border: 1px solid #a5f3fc;
          border-radius: 999px;
          padding: 7px 12px;
          font-size: 12px;
          font-weight: 900;
          margin-bottom: 14px;
        }
        .pos-lock-title {
          margin: 0;
          color: #0f172a;
          font-size: 24px;
          font-weight: 950;
          letter-spacing: -0.04em;
        }
        .pos-lock-text {
          color: #64748b;
          margin: 8px 0 18px;
          font-size: 13px;
          line-height: 1.6;
          font-weight: 700;
        }
        .pos-lock-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 12px;
        }
        .pos-lock-field label {
          color: #334155;
          font-size: 12px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .pos-lock-field input {
          height: 42px;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          padding: 0 12px;
          font-size: 14px;
          font-weight: 800;
          outline: none;
        }
        .pos-lock-error {
          background: #fef2f2;
          color: #b91c1c;
          border: 1px solid #fecaca;
          padding: 10px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 12px;
        }
        .pos-lock-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          flex-wrap: wrap;
          margin-top: 16px;
        }
        .pos-lock-primary,
        .pos-lock-secondary,
        .pos-lock-danger {
          border: none;
          border-radius: 999px;
          padding: 10px 16px;
          font-size: 13px;
          font-weight: 950;
          cursor: pointer;
        }
        .pos-lock-primary {
          background: #0f172a;
          color: #ffffff;
        }
        .pos-lock-secondary {
          background: #f1f5f9;
          color: #334155;
        }
        .pos-lock-danger {
          background: #dc2626;
          color: #ffffff;
        }
      `}</style>
      <form className="pos-lock-card" onSubmit={onUnlock} onClick={(e) => e.stopPropagation()}>
        <div className="pos-lock-badge">POS Locked</div>
        <h2 className="pos-lock-title">Screen is locked</h2>
        <p className="pos-lock-text">
          Fullscreen mode was exited or minimized. Enter the session password to continue using POS, or logout and login again.
        </p>

        {error && <div className="pos-lock-error">{error}</div>}

        <div className="pos-lock-field">
          <label>Unlock Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder="Enter session password"
            autoFocus
          />
        </div>

        <div className="pos-lock-actions">
          <button type="button" className="pos-lock-danger" onClick={onLogout}>
            Logout
          </button>
          <button type="submit" className="pos-lock-primary">
            Unlock POS
          </button>
        </div>
      </form>
    </div>
  );
};

function PosData() {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const logoutHandler = authContext?.logout || authContext?.logoutUser || authContext?.handleLogout;
  const storeId = user?.store;
  const { Post, Get, Patch, GetPaginatedData, getAPI } = useApi();
  const triggerCashDrawerPop = async () => {
    try {
      const drawers = await Get(`${getAPI('posCashDrawers')}?store_id=${storeId}`);
      const defaultDrawer = drawers?.find(d => d.is_default || d.enabled) || drawers?.[0];
      if (!defaultDrawer) return;

      if (defaultDrawer.connection_type === "PRINTER_DRIVEN" && defaultDrawer.printer_id) {
        const printers = await Get(`${getAPI('posPrinters')}?store_id=${storeId}`);
        const linkedPrinter = printers?.find(p => p.id === defaultDrawer.printer_id);
        if (linkedPrinter && linkedPrinter.connection_type === "NETWORK") {
          const kickUrl = `http://${linkedPrinter.ip_address}:${linkedPrinter.port || 9100}/kick-drawer`;
          await fetch(kickUrl, { method: "POST", mode: "no-cors" });
        }
      } else if (defaultDrawer.connection_type === "SERIAL" || defaultDrawer.connection_type === "USB") {
        if (defaultDrawer.ip_address) {
          const kickUrl = `http://${defaultDrawer.ip_address}:${defaultDrawer.port || 9100}/kick-drawer`;
          await fetch(kickUrl, { method: "POST", mode: "no-cors" });
        }
      }
    } catch (err) {
      console.warn("Auto drawer kick failed:", err);
    }
  };
  const { handleToast } = useToastHandler();
  const { formatCurrency, formatDate } = useFormattingUtils();

  const [selectedPayMethod, setSelectedPayMethod] = useState("cash");
  const [terminals, setTerminals] = useState([]);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [showTerminalModal, setShowTerminalModal] = useState(false);
  const [terminalStatus, setTerminalStatus] = useState("IDLE");
  const [terminalMessage, setTerminalMessage] = useState("");
  const pollingRef = useRef(null);

  useEffect(() => {
    if (storeId) {
      Get(`${getAPI('paymentTerminals')}?store_id=${storeId}`)
        .then((data) => {
          setTerminals(data || []);
          if (data && data.length > 0) {
            const def = data.find((t) => t.is_default) || data[0];
            setSelectedTerminal(def);
          }
        })
        .catch((err) => console.error("Error fetching terminals:", err));

      Get(`${getAPI('configurationForStore')}?store=${storeId}`)
        .then((res) => {
          const cfg = res?.results?.[0] || res?.data?.[0] || res?.[0] || res;
          if (cfg && cfg.default_pay_method) {
            setSelectedPayMethod(cfg.default_pay_method);
          }
        })
        .catch((err) => console.error("Error fetching store config:", err));
    }
  }, [storeId]);

  const [isFlipped, setIsFlipped] = useState(false);
  const [isProductFlipped, setIsProductFlipped] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [nullPriceWarning, setNullPriceWarning] = useState(null);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [receiptData, setReceiptData] = useState(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedField, setSelectedField] = useState({
    productIndex: null,
    fieldType: null,
    currentValue: "",
  });
  const [layoutPrefs, setLayoutPrefs] = useState(() => ({
    ...DEFAULT_POS_LAYOUT_PREFS,
    ...loadJsonFromStorage(POS_LAYOUT_PREFS_KEY, {}),
  }));
  const [productLocalMeta, setProductLocalMeta] = useState(() => loadJsonFromStorage(POS_PRODUCT_META_KEY, {}));
  const [allProducts, setAllProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [transactionData, setTransactionData] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionTotalPages, setTransactionTotalPages] = useState(1);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [isLockSetupOpen, setIsLockSetupOpen] = useState(false);
  const [isAppLocked, setIsAppLocked] = useState(() => {
    try {
      return sessionStorage.getItem(POS_LOCK_ACTIVE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [isFullscreenLockEnabled, setIsFullscreenLockEnabled] = useState(() => {
    try {
      return sessionStorage.getItem(POS_LOCK_ENABLED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [hasSkippedFullscreenLock, setHasSkippedFullscreenLock] = useState(() => {
    try {
      return sessionStorage.getItem(POS_LOCK_SKIPPED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [sessionLockPassword, setSessionLockPassword] = useState(() => {
    try {
      return sessionStorage.getItem(POS_LOCK_PASSWORD_KEY) || "";
    } catch {
      return "";
    }
  });
  const [lockPasswordInput, setLockPasswordInput] = useState("");
  const [lockPasswordConfirm, setLockPasswordConfirm] = useState("");
  const [unlockPasswordInput, setUnlockPasswordInput] = useState("");
  const [lockError, setLockError] = useState("");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllProducts();
    fetchTransactions();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(POS_LAYOUT_PREFS_KEY, JSON.stringify(layoutPrefs));
    } catch (error) {
      console.warn("Unable to save POS layout preferences:", error);
    }
  }, [layoutPrefs]);

  useEffect(() => {
    try {
      localStorage.setItem(POS_PRODUCT_META_KEY, JSON.stringify(productLocalMeta));
    } catch (error) {
      console.warn("Unable to save POS product metadata:", error);
    }
  }, [productLocalMeta]);

  useEffect(() => {
    if (isAppLocked) {
      setIsLockSetupOpen(false);
      setShowProductModal(false);
      setSelectedBlock(null);
      setIsProductFlipped(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isAppLocked]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenActive = !!document.fullscreenElement;
      setIsFullscreen(fullscreenActive);

      if (fullscreenActive && !sessionLockPassword && !hasSkippedFullscreenLock) {
        setIsLockSetupOpen(true);
        setLockError("");
      }

      if (!fullscreenActive && isFullscreenLockEnabled && sessionLockPassword) {
        try {
          sessionStorage.setItem(POS_LOCK_ACTIVE_KEY, "true");
        } catch (error) {
          console.warn("Unable to persist POS lock state:", error);
        }

        setIsLockSetupOpen(false);
        setShowProductModal(false);
        setSelectedBlock(null);
        setIsProductFlipped(false);
        setIsAppLocked(true);
        setUnlockPasswordInput("");
        setLockError("Fullscreen was exited. Enter session password to unlock POS.");
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [sessionLockPassword, isFullscreenLockEnabled, hasSkippedFullscreenLock]);

  const fetchAllProducts = async () => {
    try {
      const response = await Get('posItem');
      if (response) {
        const sortedResults = response.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        const newBlocks = Array(56).fill(null).map((_, index) => ({ title: '', price: 0, color: '', index, isSet: false }));
        const filledIndices = new Set();
        sortedResults.forEach((item) => {
          const index = parseInt(item.index);
          if (!isNaN(index) && index >= 0 && index < 56 && !filledIndices.has(index)) {
            newBlocks[index] = { id: item.id, title: item.title, price: item.price, color: item.color || item.colour, index, isSet: true };
            filledIndices.add(index);
          }
        });
        setAllProducts(newBlocks);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchTransactions = async (page = 1) => {
    setTransactionLoading(true);
    try {
      const response = await GetPaginatedData('getPosInvoice', {
        page,
        queryParams: { page_size: 10 }
      });
      const results = response?.data || [];
      const totalCount = response?.totalCount || 0;
      const formattedData = results.map((invoice) => ({
        id: invoice.id,
        invoice_no: invoice.invoice_no,
        date: invoice.date ? formatDate(invoice.date) : 'N/A',
        qty: invoice.total_qty,
        total_amount: invoice.total_amount,
        items: invoice.items
      }));
      setTransactionData(formattedData);
      setTransactionTotalPages(Math.ceil(totalCount / 10));
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactionData([]);
    } finally {
      setTransactionLoading(false);
    }
  };

  const buildReceiptDataFromInvoice = useCallback((invoiceData) => {
    const invoice = invoiceData?.invoice || invoiceData || {};
    const invoiceItems =
      invoiceData?.items ||
      invoiceData?.invoice_items ||
      invoiceData?.pos_invoice_products ||
      invoiceData?.sales_invoice_products ||
      invoice?.items ||
      [];

    const normalizedItems = Array.isArray(invoiceItems)
      ? invoiceItems.map((item) => {
          const qty = parseInt(item.qty || item.quantity || item.sold_qty_stock || 0, 10) || 0;
          const rate = parseFloat(item.amount_per_item || item.selling_price || item.rate || item.price || 0) || 0;
          const total = parseFloat(item.total_amount || item.amount || item.after_discount || item.total || rate * qty || 0) || 0;

          return {
            pos_item_title:
              item.pos_item_title ||
              item.product_name ||
              item.title ||
              item.name ||
              item.product ||
              "Item",
            amount_per_item: rate,
            qty,
            total_amount: total,
          };
        })
      : [];

    return {
      invoice: {
        id: invoice.id,
        invoice_no: invoice.invoice_no || invoiceData?.invoice_no || "N/A",
        date: invoice.date || invoiceData?.date || new Date().toISOString().split("T")[0],
        total_amount: invoice.total_amount || invoice.payable_amount || invoiceData?.total_amount || 0,
        total_qty:
          invoice.total_qty ||
          invoice.qty ||
          normalizedItems.reduce((sum, item) => sum + (parseInt(item.qty, 10) || 0), 0),
        pay_method: invoice.pay_method || invoice.payment_method || invoiceData?.pay_method || "Cash / POS",
      },
      items: normalizedItems,
      total_amount: invoice.total_amount || invoice.payable_amount || invoiceData?.total_amount || 0,
      date: invoice.date || invoiceData?.date || new Date().toISOString().split("T")[0],
      invoice_no: invoice.invoice_no || invoiceData?.invoice_no || "N/A",
      pay_method: invoice.pay_method || invoice.payment_method || invoiceData?.pay_method || "Cash / POS",
    };
  }, []);

  const fetchTransactionInvoiceById = useCallback(async (invoiceId) => {
    let invoiceData = null;

    const directResponse = await GetPaginatedData('getPosInvoice', {
      page: undefined,
      queryParams: { id: invoiceId }
    });

    if (Array.isArray(directResponse?.data) && directResponse.data.length > 0) {
      invoiceData = directResponse.data.find(inv => inv.id === invoiceId);
    }

    if (!invoiceData && directResponse?.data && typeof directResponse.data === "object") {
      invoiceData = directResponse.data.id === invoiceId ? directResponse.data : null;
    }

    if (!invoiceData) {
      const MAX_PAGES = 10;
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const chunk = await GetPaginatedData('getPosInvoice', {
          page,
          queryParams: { page_size: 10 }
        });

        if (Array.isArray(chunk?.data)) {
          invoiceData = chunk.data.find(inv => inv.id === invoiceId);
          if (invoiceData) break;
        }

        if (!chunk?.nextPage) break;
      }
    }

    return invoiceData;
  }, [GetPaginatedData]);

  const handleTransactionPrint = async (invoiceId) => {
    try {
      const invoiceData = await fetchTransactionInvoiceById(invoiceId);

      if (invoiceData) {
        setReceiptData(buildReceiptDataFromInvoice(invoiceData));
        setIsReceiptOpen(true);
      } else {
        handleToast({ type: "ERROR", message: `Invoice ID ${invoiceId} not found.` });
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      handleToast({ type: "ERROR", message: "Unable to open receipt preview." });
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  const handleEnableFullscreenLock = (event) => {
    event.preventDefault();

    if (!lockPasswordInput || lockPasswordInput.length < 4) {
      setLockError("Please enter at least 4 characters for the session password.");
      return;
    }

    if (lockPasswordInput !== lockPasswordConfirm) {
      setLockError("Password and confirm password do not match.");
      return;
    }

    try {
      sessionStorage.setItem(POS_LOCK_PASSWORD_KEY, lockPasswordInput);
      sessionStorage.setItem(POS_LOCK_ENABLED_KEY, "true");
      sessionStorage.setItem(POS_LOCK_SKIPPED_KEY, "false");
      sessionStorage.removeItem(POS_LOCK_ACTIVE_KEY);
    } catch (error) {
      console.warn("Unable to persist fullscreen lock settings:", error);
    }

    setSessionLockPassword(lockPasswordInput);
    setIsFullscreenLockEnabled(true);
    setHasSkippedFullscreenLock(false);
    setIsAppLocked(false);
    setIsLockSetupOpen(false);
    setLockPasswordInput("");
    setLockPasswordConfirm("");
    setLockError("");
    handleToast({ type: "SUCCESS", message: "Fullscreen session lock enabled." });
  };

  const handleSkipFullscreenLock = () => {
    try {
      sessionStorage.setItem(POS_LOCK_SKIPPED_KEY, "true");
      sessionStorage.setItem(POS_LOCK_ENABLED_KEY, "false");
      sessionStorage.removeItem(POS_LOCK_PASSWORD_KEY);
      sessionStorage.removeItem(POS_LOCK_ACTIVE_KEY);
    } catch (error) {
      console.warn("Unable to persist fullscreen lock skip:", error);
    }

    setHasSkippedFullscreenLock(true);
    setIsFullscreenLockEnabled(false);
    setSessionLockPassword("");
    setIsAppLocked(false);
    setIsLockSetupOpen(false);
    setLockPasswordInput("");
    setLockPasswordConfirm("");
    setLockError("");
  };

  const handleUnlockFullscreenSession = async (event) => {
    event.preventDefault();

    let storedPassword = sessionLockPassword;

    try {
      storedPassword = storedPassword || sessionStorage.getItem(POS_LOCK_PASSWORD_KEY) || "";
    } catch {
      storedPassword = sessionLockPassword;
    }

    if (unlockPasswordInput !== storedPassword) {
      setLockError("Incorrect password. Please try again or logout.");
      return;
    }

    try {
      sessionStorage.removeItem(POS_LOCK_ACTIVE_KEY);
    } catch (error) {
      console.warn("Unable to clear active POS lock state:", error);
    }

    setSessionLockPassword(storedPassword);
    setIsFullscreenLockEnabled(true);
    setIsAppLocked(false);
    setUnlockPasswordInput("");
    setLockError("");

    if (!document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
      } catch (error) {
        console.warn("Unable to re-enter fullscreen automatically:", error);
      }
    }
  };

  const handleLockLogout = () => {
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("token");
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      localStorage.removeItem("selectedCompany");
      sessionStorage.removeItem(POS_LOCK_PASSWORD_KEY);
      sessionStorage.removeItem(POS_LOCK_ENABLED_KEY);
      sessionStorage.removeItem(POS_LOCK_SKIPPED_KEY);
      sessionStorage.removeItem(POS_LOCK_ACTIVE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn("Unable to clear session storage:", error);
    }

    if (typeof logoutHandler === "function") {
      logoutHandler();
    }

    window.location.href = "/login";
  };

  const handleBlockClick = (block, index) => {
    if (isAppLocked) return;

    const productMeta = productLocalMeta[`id:${block.id}`] || productLocalMeta[`index:${index}`] || {};
    setSelectedBlock({ ...block, index, section: productMeta.section || '' });
    setShowProductModal(true);
  };

  const handleSaveProductSection = (blockIndex, section, productId) => {
    const cleanedSection = (section || '').trim();
    setProductLocalMeta((prev) => ({
      ...prev,
      ...(productId ? { [`id:${productId}`]: { ...(prev[`id:${productId}`] || {}), section: cleanedSection } } : {}),
      [`index:${blockIndex}`]: { ...(prev[`index:${blockIndex}`] || {}), section: cleanedSection },
    }));
  };

  const handleDeleteProduct = async (e, blockId) => {
    e.stopPropagation();

    if (isAppLocked) return;
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await Patch('posItem', blockId, { is_deleted: true });
        setProductLocalMeta((prev) => {
          const next = { ...prev };
          delete next[`id:${blockId}`];
          return next;
        });
        fetchAllProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const getColor = (colour) => {
    const colorMap = { Red: "#EE6055", Blue: "#83D3F2", Green: "#ADF7B6", Lavender: "#BDB2FF", Peach: "#FFC89A" };
    return colorMap[colour] || colour || "#83D3F2";
  };

  const saveSelectedProducts = useCallback((products) => {
    if (products?.length > 0) {
      try {
        const productsToSave = products.map(product => ({
          ...product,
          price: typeof product.price === 'number' ? product.price : parseFloat(product.price) || 0,
          quantity: typeof product.quantity === 'number' ? product.quantity : parseInt(product.quantity) || 1,
          lastSaved: new Date().toISOString()
        }));
        const productsString = JSON.stringify(productsToSave);
        try {
          localStorage.setItem(STORAGE_KEY, productsString);
        } catch {
          sessionStorage.setItem(STORAGE_KEY, productsString);
        }
      } catch (error) {
        console.error('Error saving products:', error);
      }
    }
  }, []);

  const clearSelectedProducts = useCallback(() => {
    setSelectedProducts([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing products:', error);
    }
  }, []);

  useEffect(() => {
    const loadSavedProducts = () => {
      try {
        let savedProducts = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
        if (savedProducts) {
          const parsedProducts = JSON.parse(savedProducts);
          if (Array.isArray(parsedProducts) && parsedProducts.length > 0) {
            const validProducts = parsedProducts.filter(product =>
              product && typeof product === 'object' && product.title &&
              (typeof product.price === 'number' || !isNaN(parseFloat(product.price))) &&
              (typeof product.quantity === 'number' || !isNaN(parseInt(product.quantity)))
            );
            if (validProducts.length > 0) {
              const normalizedProducts = validProducts.map(product => {
                const { lastSaved, ...productWithoutTimestamp } = product;
                return {
                  ...productWithoutTimestamp,
                  uniqueId: product.uniqueId || createCartItemId(product),
                  price: typeof product.price === 'number' ? product.price : parseFloat(product.price) || 0,
                  quantity: typeof product.quantity === 'number' ? product.quantity : parseInt(product.quantity) || 1
                };
              });
              setSelectedProducts(normalizedProducts);
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error parsing saved products:', error);
      }
      try {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (clearError) {
        console.error('Error clearing corrupted data:', clearError);
      }
      setSelectedProducts([]);
    };

    loadSavedProducts();

    const handleBeforeUnload = () => {
      if (selectedProducts.length > 0) saveSelectedProducts(selectedProducts);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && selectedProducts.length > 0) {
        saveSelectedProducts(selectedProducts);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (selectedProducts.length > 0) saveSelectedProducts(selectedProducts);
  }, [selectedProducts, saveSelectedProducts]);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      const backupInterval = setInterval(() => saveSelectedProducts(selectedProducts), 30000);
      return () => clearInterval(backupInterval);
    }
  }, [selectedProducts, saveSelectedProducts]);

  useEffect(() => {
    const productWithoutPriceIndex = selectedProducts.findIndex((product) => {
      const price = parseFloat(product.price);
      return Number.isNaN(price) || price === 0;
    });
    const productWithoutPrice = productWithoutPriceIndex >= 0 ? selectedProducts[productWithoutPriceIndex] : null;
    setNullPriceWarning(productWithoutPrice ? { productName: productWithoutPrice.title, productIndex: productWithoutPriceIndex } : null);
  }, [selectedProducts]);

  const handleCloseReceipt = useCallback(() => {
    setIsReceiptOpen(false);
    setReceiptData(null);
  }, []);

  const invoiceValidation = useMemo(() => {
    if (selectedProducts.length === 0) {
      return { canCreate: false, message: "Please select at least one product." };
    }

    const hasMissingProductId = selectedProducts.some(product => !product.id);
    if (hasMissingProductId) {
      return { canCreate: false, message: "One or more cart items are missing product id. Please remove and add them again." };
    }

    const hasInvalidQty = selectedProducts.some(product => {
      const quantity = parseInt(product.quantity, 10);
      return Number.isNaN(quantity) || quantity <= 0;
    });
    if (hasInvalidQty) {
      return { canCreate: false, message: "Please enter a valid quantity for all products." };
    }

    const hasInvalidPrice = selectedProducts.some(product => {
      const price = parseFloat(product.price);
      return Number.isNaN(price) || price === 0;
    });
    if (hasInvalidPrice) {
      return { canCreate: false, message: "Please enter a valid price for all products." };
    }

    return { canCreate: true, message: "Ready to create invoice." };
  }, [selectedProducts]);

  const canCreateInvoice = invoiceValidation.canCreate && !isCreatingInvoice;

  const pollTerminalPayment = (paymentId, payload) => {
    let attempts = 0;
    if (pollingRef.current) clearInterval(pollingRef.current);
    
    pollingRef.current = setInterval(() => {
      attempts += 1;
      if (attempts > 60) {
        clearInterval(pollingRef.current);
        setTerminalStatus("FAILED");
        setTerminalMessage("Payment timeout. Please try again.");
        return;
      }

      Get(`${getAPI('paymentsTerminal')}${paymentId}/`)
        .then((response) => {
          const status = response.status;
          setTerminalStatus(status);
          if (status === "WAITING_FOR_CUSTOMER") {
            setTerminalMessage(`Please tap, insert, or swipe card on ${selectedTerminal?.name || "the terminal"}...`);
          } else if (status === "PROCESSING") {
            setTerminalMessage("Processing payment...");
          } else if (status === "SUCCESS") {
            clearInterval(pollingRef.current);
            setTerminalMessage("Approved! Saving bill...");
            
            const updatedPayload = {
              ...payload,
              pay_method: "bank"
            };
            
            Post("createPosInvoice", updatedPayload)
              .then((invoiceRes) => {
                if (!invoiceRes?.data?.invoice) {
                  throw new Error("Invalid response");
                }
                const invoice = invoiceRes.data.invoice;
                const invoiceItems = invoiceRes.data.items;

                handleToast({ type: "SUCCESS", message: "POS Invoice created successfully via Terminal!" });

                const receiptPrintData = {
                  invoice: {
                    id: invoice.id,
                    invoice_no: invoice.invoice_no,
                    date: invoice.date,
                    total_amount: invoice.total_amount,
                    total_qty: invoice.total_qty,
                  },
                  items: invoiceItems.map((item) => ({
                    pos_item_title: item.pos_item_title,
                    amount_per_item: item.amount_per_item,
                    qty: item.qty,
                    total_amount: item.total_amount,
                  })),
                  total_amount: invoice.total_amount,
                  date: invoice.date,
                  invoice_no: invoice.invoice_no,
                };

                setReceiptData(receiptPrintData);
                setIsReceiptOpen(true);
                clearSelectedProducts();
                setInvoiceCount((prev) => prev + 1);
                fetchTransactions(1);
              })
              .catch((err) => {
                handleToast({ type: "ERROR", message: "Failed to save POS invoice." });
              })
              .finally(() => {
                setShowTerminalModal(false);
              });
          }
        })
        .catch((err) => {
          console.error("Error checking terminal status:", err);
        });
    }, 3000);
  };

  const handleCreateInvoice = useCallback(async () => {
    if (isAppLocked) return;

    if (isCreatingInvoice) return;

    if (!invoiceValidation.canCreate) {
      handleToast({ type: "ERROR", message: invoiceValidation.message });
      return;
    }

    const productsWithValidation = selectedProducts.map(product => {
      const price = parseFloat(product.price) || 0;
      const quantity = parseInt(product.quantity) || 0;
      const total = price * quantity;
      return {
        ...product,
        price: price.toFixed(2),
        quantity,
        total,
        isValid: quantity > 0 && price !== 0 && !isNaN(total)
      };
    });

    const validProducts = productsWithValidation.filter(product => product.isValid);

    if (validProducts.length === 0) {
      handleToast({ type: "ERROR", message: "Please add at least one product with a valid quantity." });
      return;
    }

    const totalPrice = validProducts.reduce((total, product) => {
      const productTotal = parseFloat(product.price) * parseInt(product.quantity || 0);
      return total + productTotal;
    }, 0);

    if (totalPrice < 0) {
      handleToast({ type: "ERROR", message: "Total invoice amount cannot be negative." });
      return;
    }

    setIsCreatingInvoice(true);

    try {
      const items = validProducts.map((product) => ({
        pos_item: product.id,
        amount_per_item: product.price,
        qty: product.quantity,
      }));

      const payload = {
        store: storeId,
        date: new Date().toISOString().split("T")[0],
        items: items,
        pay_method: selectedPayMethod,
        has_negative_price_items: validProducts.some(p => p.price < 0)
      };

      if (selectedPayMethod === "terminal") {
        if (!selectedTerminal) {
          handleToast({ type: "ERROR", message: "Please configure a payment terminal in Settings first." });
          setIsCreatingInvoice(false);
          return;
        }
        setShowTerminalModal(true);
        setTerminalStatus("CONNECTING");
        setTerminalMessage(`Connecting to ${selectedTerminal.name}...`);
        
        Post("paymentsTerminal", {
          store_id: storeId,
          terminal_id: selectedTerminal.id,
          amount_minor: Math.round(totalPrice * 100)
        })
          .then(async (res) => {
            const data = res.data;
            const dispatch = data.dispatch;

            // For a real LOCAL_LAN terminal (e.g. Clover on the store's own
            // Wi-Fi), the backend can't reach the device itself - it runs in
            // our cloud cluster, not on the store's network. It hands back
            // `dispatch` details instead, and THIS browser (which IS on the
            // same LAN as the till) has to actually push the amount to the
            // device, exactly like the Settings "Test Connection" button
            // does (PaymentTerminalSettings.js). Skipping this step is why
            // the amount was never reaching the terminal - polling alone
            // just waits forever for a charge that was never sent.
            if (dispatch && dispatch.required && dispatch.type === "CLOVER_WS") {
              setTerminalMessage(`Sending amount to ${selectedTerminal.name} over Wi-Fi...`);
              try {
                const saleResponse = await runCloverLocalSale({
                  uri: dispatch.uri,
                  applicationId: dispatch.applicationId,
                  posName: dispatch.posName,
                  serialNumber: dispatch.serialNumber,
                  authToken: dispatch.authToken,
                  amountCents: dispatch.amount,
                  externalId: dispatch.externalId,
                  onPairingCode: (code) => {
                    setTerminalMessage(`Pairing code on ${selectedTerminal.name}: ${code} — confirm on the terminal's screen (first time only).`);
                  },
                  onAuthToken: (newAuthToken) => {
                    Patch("paymentTerminals", selectedTerminal.id, { clover_ws_auth_token: newAuthToken }).catch(() => {});
                  },
                  onStatus: (msg) => setTerminalMessage(msg),
                });
                await Post(`${getAPI('paymentsTerminal')}${data.payment_id}/report/`, {
                  status: "WAITING_FOR_CUSTOMER",
                  provider_transaction_id: saleResponse?.getPayment?.()?.getId?.() || undefined,
                }).catch(() => {});
              } catch (dispatchErr) {
                console.error("Failed to send amount to terminal:", dispatchErr);
                const raw = dispatchErr?.message || String(dispatchErr);
                setTerminalStatus("FAILED");
                setTerminalMessage(
                  `Could not complete the charge on ${selectedTerminal.name}: ${raw} Make sure this till and the terminal are on the same Wi-Fi and Network Pay Display is started on the device.`
                );
                await Post(`${getAPI('paymentsTerminal')}${data.payment_id}/report/`, {
                  status: "FAILED",
                  failure_message: raw,
                }).catch(() => {});
                setIsCreatingInvoice(false);
                return;
              }
            } else if (dispatch && dispatch.required) {
              // Legacy REST-based local dispatch (other providers/models).
              setTerminalMessage(`Sending amount to ${selectedTerminal.name}...`);
              try {
                const dispatchRes = await fetch(dispatch.url, {
                  method: dispatch.method || "POST",
                  headers: dispatch.headers || { "Content-Type": "application/json" },
                  body: JSON.stringify(dispatch.payload),
                });
                if (!dispatchRes.ok) {
                  const bodyText = await dispatchRes.text().catch(() => "");
                  throw new Error(`Terminal responded with HTTP ${dispatchRes.status}${bodyText ? `: ${bodyText}` : ""}`);
                }
                const dispatchData = await dispatchRes.json().catch(() => ({}));
                await Post(`${getAPI('paymentsTerminal')}${data.payment_id}/report/`, {
                  status: "WAITING_FOR_CUSTOMER",
                  provider_transaction_id: dispatchData.id || dispatchData.paymentId,
                }).catch(() => {});
              } catch (dispatchErr) {
                console.error("Failed to send amount to terminal:", dispatchErr);
                const raw = dispatchErr?.message || String(dispatchErr);
                const unreachable = dispatchErr?.name === "TypeError" || /Failed to fetch|NetworkError|Load failed/i.test(raw);
                setTerminalStatus("FAILED");
                setTerminalMessage(
                  unreachable
                    ? `Could not reach ${selectedTerminal.name} at ${selectedTerminal.machine_ip}:${selectedTerminal.port || 12345}. Make sure this till and the terminal are on the same Wi-Fi, the terminal is awake, and (first time only) you've opened https://${selectedTerminal.machine_ip}:${selectedTerminal.port || 12345}/ once in this browser to accept its certificate.`
                    : raw
                );
                await Post(`${getAPI('paymentsTerminal')}${data.payment_id}/report/`, {
                  status: "FAILED",
                  failure_message: raw,
                }).catch(() => {});
                setIsCreatingInvoice(false);
                return;
              }
            }

            pollTerminalPayment(data.payment_id, payload);
          })
          .catch((err) => {
            console.error("Error initiating POS payment:", err);
            setTerminalStatus("FAILED");
            setTerminalMessage(err.response?.data?.detail || "Failed to initiate payment on terminal.");
          })
          .finally(() => {
            setIsCreatingInvoice(false);
          });
        return;
      }

      const response = await Post("createPosInvoice", payload);

      if (!response?.data?.invoice) {
        throw new Error("Invalid response: Missing invoice details");
      }

      const invoice = response.data.invoice;
      const invoiceItems = response.data.items;

      handleToast({ type: "SUCCESS", message: `${response.message || "Invoice created successfully"}` });
      if (selectedPayMethod === "cash") {
        triggerCashDrawerPop();
      }

      const receiptPrintData = {
        invoice: {
          id: invoice.id,
          invoice_no: invoice.invoice_no,
          date: invoice.date,
          total_amount: invoice.total_amount,
          total_qty: invoice.total_qty,
        },
        items: invoiceItems.map((item) => ({
          pos_item_title: item.pos_item_title,
          amount_per_item: item.amount_per_item,
          qty: item.qty,
          total_amount: item.total_amount,
        })),
        total_amount: invoice.total_amount,
        date: invoice.date,
        invoice_no: invoice.invoice_no,
      };

      setReceiptData(receiptPrintData);
      setIsReceiptOpen(true);
      clearSelectedProducts();
      setInvoiceCount((prev) => prev + 1);
      fetchTransactions(1);
    } catch (error) {
      console.error("Invoice creation error:", error);
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Unknown error";
      handleToast({ type: "ERROR", message: `Invoice creation failed: ${apiMessage}` });
    } finally {
      setIsCreatingInvoice(false);
    }
  }, [selectedProducts, storeId, Post, handleToast, clearSelectedProducts, isCreatingInvoice, invoiceValidation, isAppLocked]);

  const handleFieldSelect = useCallback((productIndex, fieldType) => {
    if (productIndex < 0 || productIndex >= selectedProducts.length) {
      productIndex = selectedProducts.length - 1;
    }
    if (selectedProducts[productIndex]) {
      setSelectedField({ productIndex, fieldType, currentValue: "" });
    }
  }, [selectedProducts]);

  const handleQuantityBlockClick = useCallback(() => {
    if (selectedProducts.length > 0) {
      const targetIndex = selectedField.productIndex !== null ? selectedField.productIndex : selectedProducts.length - 1;
      setSelectedField({ productIndex: targetIndex, fieldType: "quantity", currentValue: "" });
    }
  }, [selectedProducts, selectedField.productIndex]);

  const handlePriceBlockClick = useCallback(() => {
    if (selectedProducts.length > 0) {
      const targetIndex = selectedField.productIndex !== null ? selectedField.productIndex : selectedProducts.length - 1;
      setSelectedField({ productIndex: targetIndex, fieldType: "price", currentValue: "" });
    }
  }, [selectedProducts, selectedField.productIndex]);

  useEffect(() => {
    const handleKeyboardShortcut = (event) => {
      if (event.altKey && !isMobile) {
        event.preventDefault();
        switch (event.key.toLowerCase()) {
          case 'q':
            if (selectedProducts.length > 0) {
              const targetIndex = selectedField.productIndex !== null ? selectedField.productIndex : selectedProducts.length - 1;
              setSelectedField({ productIndex: targetIndex, fieldType: "quantity", currentValue: "" });
            }
            break;
          case 'p':
            if (selectedProducts.length > 0) {
              const targetIndex = selectedField.productIndex !== null ? selectedField.productIndex : selectedProducts.length - 1;
              setSelectedField({ productIndex: targetIndex, fieldType: "price", currentValue: "" });
            }
            break;
        }
      }
    };
    window.addEventListener('keydown', handleKeyboardShortcut);
    return () => window.removeEventListener('keydown', handleKeyboardShortcut);
  }, [selectedProducts, selectedField, isMobile]);

  const handleFieldChange = useCallback(async (productIndex, fieldType, value) => {
    const updatedProducts = [...selectedProducts];
    const product = { ...updatedProducts[productIndex] };

    if (fieldType === "quantity") {
      product.quantity = value;
    } else if (fieldType === "price") {
      product.tempTotalPrice = value;
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        const currentQuantity = parseInt(product.quantity || 1);
        const newPrice = numericValue / currentQuantity;
        product.price = newPrice;
        
        // Update product price in database immediately
        if (product.id && newPrice !== 0) {
          try {
            await Patch('posItem', product.id, { price: newPrice });
            await fetchAllProducts();
          } catch (err) {
            console.error('Error updating product price:', err);
          }
        }
      }
      if (product.price && product.price !== "" && parseFloat(product.price) !== 0 && nullPriceWarning && nullPriceWarning.productIndex === productIndex) {
        setNullPriceWarning(null);
      }
    }

    updatedProducts[productIndex] = product;
    setSelectedProducts(updatedProducts);
  }, [selectedProducts, nullPriceWarning, Patch, fetchAllProducts]);

  const handleNumericKeyPress = useCallback(async (key) => {
    if (selectedField.productIndex === null || selectedField.fieldType === null) {
      if (key === "OK") {
        await handleCreateInvoice();
      }
      return;
    }

    const { productIndex, fieldType } = selectedField;
    let newValue = selectedField.currentValue;

    if (key === "remove") {
      if (newValue.length > 0) newValue = newValue.slice(0, -1);
    } else if (key === "OK") {
      if (newValue !== "") {
        const updatedProducts = [...selectedProducts];
        const product = { ...updatedProducts[productIndex] };

        if (fieldType === "quantity") {
          const numericValue = parseInt(newValue, 10);
          product.quantity = isNaN(numericValue) ? 1 : Math.max(1, numericValue);
        } else if (fieldType === "price") {
          const numericValue = parseFloat(newValue);
          if (!isNaN(numericValue)) {
            const currentQuantity = parseInt(product.quantity || 1);
            const newPrice = numericValue / currentQuantity;
            product.price = newPrice;
            delete product.tempTotalPrice;
            
            // Update product price in database
            if (product.id && newPrice !== 0) {
              try {
                await Patch('posItem', product.id, { price: newPrice });
                await fetchAllProducts();
              } catch (err) {
                console.error('Error updating product price:', err);
              }
            }
            
            if (numericValue !== 0 && nullPriceWarning && nullPriceWarning.productIndex === productIndex) {
              setNullPriceWarning(null);
            }
          }
        }

        updatedProducts[productIndex] = product;
        setSelectedProducts(updatedProducts);
      }
      setSelectedField({ productIndex: null, fieldType: null, currentValue: "" });
      return;
    } else if (key === "-") {
      if (fieldType !== "price") return;
      newValue = newValue.startsWith("-") ? newValue.slice(1) : `-${newValue}`;
    } else {
      const tempNewValue = newValue + key.toString();
      if (fieldType === "quantity") {
        const numericValue = tempNewValue.replace(/\D/g, '');
        if (numericValue.length > 3) return;
      } else if (fieldType === "price") {
        const digitCount = tempNewValue.replace(/[^0-9]/g, "").length;
        if (digitCount > 5) return;
      }
      newValue = tempNewValue;
    }

    const updatedProducts = [...selectedProducts];
    const product = { ...updatedProducts[productIndex] };

    if (fieldType === "quantity") {
      const numericValue = parseInt(newValue, 10);
      product.quantity = newValue === "" ? "" : (isNaN(numericValue) ? 1 : Math.max(1, numericValue));
    } else if (fieldType === "price") {
      if (newValue === "" || newValue === "-") {
        product.price = "";
        product.tempTotalPrice = newValue;
      } else {
        const parsedPrice = parseFloat(newValue);
        product.price = isNaN(parsedPrice) ? "" : parsedPrice;
        product.tempTotalPrice = parsedPrice;
      }
      const numericValue = parseFloat(newValue);
      if (!isNaN(numericValue) && numericValue !== 0 && nullPriceWarning) {
        setNullPriceWarning(null);
      }
    }

    updatedProducts[productIndex] = product;
    setSelectedProducts(updatedProducts);
    setSelectedField({ ...selectedField, currentValue: newValue });
  }, [selectedField, selectedProducts, nullPriceWarning, Patch, fetchAllProducts, handleCreateInvoice]);

  const addProductToContainer = useCallback((product) => {
    if (isAppLocked) return;

    if (!product?.isSet || !product?.id) {
      handleToast({ type: "ERROR", message: "This product is not configured properly." });
      return;
    }

    setSelectedProducts((prev) => {
      const parsedPrice = parseFloat(product.price);
      const roundedPrice = Number.isNaN(parsedPrice) ? 0 : Math.round(parsedPrice * 100) / 100;
      const existingProductIndex = prev.findIndex(p => p.id === product.id && parseFloat(p.price) === roundedPrice);
      
      if (existingProductIndex !== -1) {
        return prev.map((p, index) => 
          index === existingProductIndex 
            ? { ...p, quantity: (parseInt(p.quantity, 10) || 0) + 1, price: roundedPrice.toFixed(2) }
            : p
        );
      }

      return [
        ...prev,
        {
          ...product,
          uniqueId: createCartItemId(product),
          quantity: 1,
          price: roundedPrice.toFixed(2),
        },
      ];
    });
  }, [handleToast, isAppLocked]);

  const updateLayoutPref = useCallback((key, value) => {
    setLayoutPrefs((prev) => ({ ...prev, [key]: value }));
  }, []);

  const getProductSection = useCallback((block, index) => {
    const productMeta = productLocalMeta[`id:${block.id}`] || productLocalMeta[`index:${index}`] || {};
    return (productMeta.section || '').trim();
  }, [productLocalMeta]);

  const groupedProductBlocks = useMemo(() => {
    if (!layoutPrefs.groupBySection) {
      return [{ title: '', blocks: allProducts }];
    }

    const groups = new Map();
    allProducts.forEach((block, index) => {
      const section = block.isSet ? getProductSection(block, index) || 'Unsectioned' : 'Available Slots';
      if (!groups.has(section)) groups.set(section, []);
      groups.get(section).push({ ...block, originalIndex: index });
    });

    return Array.from(groups.entries())
      .sort(([sectionA], [sectionB]) => {
        if (sectionA === 'Available Slots') return 1;
        if (sectionB === 'Available Slots') return -1;
        return sectionA.localeCompare(sectionB);
      })
      .map(([title, blocks]) => ({ title, blocks }));
  }, [allProducts, getProductSection, layoutPrefs.groupBySection]);

  const PosLayoutControls = useCallback(() => (
    <div className="pos-layout-controls">
      <label className="pos-layout-control">
        <span>Panel</span>
        <select value={layoutPrefs.panelPosition} onChange={(e) => updateLayoutPref('panelPosition', e.target.value)}>
          <option value="left">Left</option>
          <option value="right">Right</option>
          <option value="top">Top</option>
          <option value="bottom">Bottom</option>
        </select>
      </label>
      <label className="pos-layout-control">
        <span>Card</span>
        <select value={layoutPrefs.cardSize} onChange={(e) => updateLayoutPref('cardSize', e.target.value)}>
          {Object.entries(CARD_SIZE_CONFIG).map(([value, config]) => (
            <option value={value} key={value}>{config.label}</option>
          ))}
        </select>
      </label>
      <label className="pos-layout-toggle">
        <input
          type="checkbox"
          checked={layoutPrefs.groupBySection}
          onChange={(e) => updateLayoutPref('groupBySection', e.target.checked)}
        />
        <span>Sections</span>
      </label>
    </div>
  ), [layoutPrefs, updateLayoutPref]);

  const ProductManagementView = useCallback(() => {
    const cardSize = CARD_SIZE_CONFIG[layoutPrefs.cardSize] || CARD_SIZE_CONFIG.medium;
    const panelStyle = {
      padding: 'clamp(10px, 1.1vw, 16px)',
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
    };

    const headerStyle = {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px',
      gap: '12px',
      flexShrink: 0,
    };

    const gridStyle = {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${cardSize.min}px, 1fr))`,
      gridAutoRows: `${cardSize.row}px`,
      gap: 'clamp(6px, 0.75vw, 12px)',
      alignContent: 'start',
    };

    const getProductCardStyle = (block) => ({
      backgroundColor: block.isSet ? getColor(block.color) : 'transparent',
      borderRadius: '10px',
      border: block.isSet ? '1px solid rgba(0,0,0,0.08)' : '1px dashed rgba(148, 163, 184, 0.22)',
      cursor: block.isSet ? 'pointer' : 'default',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(5px, 0.55vw, 9px)',
      minWidth: 0,
      minHeight: 0,
      boxSizing: 'border-box',
      boxShadow: block.isSet ? '0 3px 8px rgba(15, 23, 42, 0.10)' : 'none',
      transition: 'transform 0.12s ease, box-shadow 0.12s ease',
      overflow: 'hidden',
      opacity: block.isSet ? 1 : 0.36,
    });

    return (
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1f2937', lineHeight: 1.1 }}>Products</h3>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px' }}>Tap product to add into bill</div>
          </div>
          <div className="pos-product-header-actions">
            <PosLayoutControls />
            {!isAppLocked && (
              <button
                onClick={() => setIsProductFlipped(true)}
                style={{
                  padding: '8px 14px',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '13px',
                  boxShadow: '0 3px 8px rgba(37, 99, 235, 0.20)',
                }}
              >
                Configure
              </button>
            )}
            <button
              onClick={toggleFullscreen}
              style={{
                width: '36px',
                height: '36px',
                background: '#16a34a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 3px 8px rgba(22, 163, 74, 0.20)',
              }}
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? <FullscreenExitIcon style={{ fontSize: '20px' }} /> : <FullscreenIcon style={{ fontSize: '20px' }} />}
            </button>
          </div>
        </div>

        <div className="pos-product-scroll">
          {groupedProductBlocks.map((group, groupIndex) => (
            <div className="pos-product-section" key={`${group.title}-${groupIndex}`}>
              {layoutPrefs.groupBySection && group.title && (
                <div className="pos-product-section-title">{group.title}</div>
              )}
              <div style={gridStyle}>
                {group.blocks.map((block, blockIndex) => {
                  const index = block.originalIndex ?? blockIndex;
                  return (
                    <div
                      key={index}
                      className={block.isSet ? "pos-product-card" : "pos-empty-product-card"}
                      style={getProductCardStyle(block)}
                      onClick={() => block.isSet && addProductToContainer(block)}
                      title={block.isSet ? `${block.title} - ₹${parseFloat(block.price || 0).toFixed(2)}` : `Empty slot ${index + 1}`}
                    >
                      {block.isSet ? (
                        <>
                          <div
                            className="pos-product-card-title"
                            style={{
                              width: '100%',
                              textAlign: 'center',
                              color: '#111827',
                              fontSize: 'clamp(10px, 0.9vw, 14px)',
                              fontWeight: 800,
                              lineHeight: 1.12,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                            }}
                          >
                            {block.title}
                          </div>
                          <div className="pos-product-price-pill">
                            ₹{parseFloat(block.price || 0).toFixed(2)}
                          </div>
                        </>
                      ) : (
                        <span className="pos-empty-slot-number">{index + 1}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }, [PosLayoutControls, addProductToContainer, groupedProductBlocks, isFullscreen, isAppLocked, layoutPrefs]);

  const ProductConfigureView = useCallback(() => {
    const cardSize = CARD_SIZE_CONFIG[layoutPrefs.cardSize] || CARD_SIZE_CONFIG.medium;
    const panelStyle = {
      padding: 'clamp(10px, 1.1vw, 16px)',
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
    };

    const headerStyle = {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px',
      gap: '12px',
      flexShrink: 0,
    };

    const gridStyle = {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${cardSize.min}px, 1fr))`,
      gridAutoRows: `${cardSize.row}px`,
      gap: 'clamp(6px, 0.75vw, 12px)',
      alignContent: 'start',
    };

    const getConfigCardStyle = (block) => ({
      backgroundColor: block.isSet ? getColor(block.color) : 'transparent',
      borderRadius: '10px',
      border: block.isSet ? '1px solid rgba(0,0,0,0.08)' : '1px dashed rgba(148, 163, 184, 0.24)',
      position: 'relative',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(5px, 0.55vw, 9px)',
      minWidth: 0,
      minHeight: 0,
      boxSizing: 'border-box',
      boxShadow: block.isSet ? '0 3px 8px rgba(15, 23, 42, 0.10)' : 'none',
      overflow: 'hidden',
      opacity: block.isSet ? 1 : 0.42,
    });

    return (
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1f2937', lineHeight: 1.1 }}>Configure Products</h3>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px' }}>Tap any slot to add or edit product</div>
          </div>
          <div className="pos-product-header-actions">
            <PosLayoutControls />
            <button
              onClick={() => setIsProductFlipped(false)}
              style={{
                padding: '8px 14px',
                background: '#4b5563',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '13px',
                flexShrink: 0,
              }}
            >
              Back
            </button>
          </div>
        </div>

        <div className="pos-product-scroll">
          {groupedProductBlocks.map((group, groupIndex) => (
            <div className="pos-product-section" key={`${group.title}-${groupIndex}`}>
              {layoutPrefs.groupBySection && group.title && (
                <div className="pos-product-section-title">{group.title}</div>
              )}
              <div style={gridStyle}>
                {group.blocks.map((block, blockIndex) => {
                  const index = block.originalIndex ?? blockIndex;
                  return (
                    <div
                      key={index}
                      className={block.isSet ? "pos-product-card" : "pos-empty-product-card pos-config-empty-card"}
                      style={getConfigCardStyle(block)}
                      onClick={() => handleBlockClick(block, index)}
                      title={block.isSet ? `Edit ${block.title}` : `Configure slot ${index + 1}`}
                    >
                      {block.isSet ? (
                        <>
                          <div
                            className="pos-product-card-title"
                            style={{
                              width: '100%',
                              textAlign: 'center',
                              color: '#111827',
                              fontSize: 'clamp(10px, 0.82vw, 13px)',
                              fontWeight: 800,
                              lineHeight: 1.12,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                              paddingRight: '14px',
                              paddingLeft: '14px',
                              boxSizing: 'border-box',
                            }}
                          >
                            {block.title}
                          </div>
                          <div className="pos-product-price-pill">
                            ₹{parseFloat(block.price || 0).toFixed(2)}
                          </div>
                          <button
                            type="button"
                            aria-label="Delete product"
                            className="pos-product-delete-button"
                            onClick={(e) => handleDeleteProduct(e, block.id)}
                          >
                            <img src={DeleteIcon} alt="Delete" style={{ width: '100%', height: '100%' }} />
                          </button>
                        </>
                      ) : (
                        <span className="pos-empty-slot-number">+ {index + 1}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }, [PosLayoutControls, groupedProductBlocks, handleBlockClick, handleDeleteProduct, layoutPrefs]);

  const handleProductDelete = useCallback((uniqueIdOrProductId) => {
    if (uniqueIdOrProductId === undefined || uniqueIdOrProductId === null) return;

    setSelectedProducts((prevProducts) => {
      const removeIndex = prevProducts.findIndex(product =>
        product.uniqueId === uniqueIdOrProductId || product.id === uniqueIdOrProductId
      );

      if (removeIndex === -1) return prevProducts;

      const updatedProducts = prevProducts.filter((_, index) => index !== removeIndex);
      if (updatedProducts.length > 0) {
        saveSelectedProducts(updatedProducts);
      } else {
        clearSelectedProducts();
      }
      return updatedProducts;
    });
  }, [saveSelectedProducts, clearSelectedProducts]);

  const getTotalAmount = useCallback(() => {
    return selectedProducts.reduce((total, product) => {
      const price = parseFloat(product.price) || 0;
      const quantity = parseInt(product.quantity) || 0;
      const productTotal = Math.round((price * quantity) * 100) / 100;
      return total + productTotal;
    }, 0).toFixed(2);
  }, [selectedProducts]);

  const handlePageChange = useCallback((newPage) => {
    setTransactionPage(newPage);
    fetchTransactions(newPage);
  }, []);

  const TransactionView = useMemo(() => {
    return () => (
      <div className="order-details-container">
        <p className="transaction-title">Recent Transactions</p>
        <TransactionTable
          data={transactionData}
          loading={transactionLoading}
          currentPage={transactionPage}
          totalPages={transactionTotalPages}
          onPageChange={handlePageChange}
          onPrint={handleTransactionPrint}
          compact={true}
          rowsPerPage={10}
        />
      </div>
    );
  }, [transactionData, transactionLoading, transactionPage, transactionTotalPages, handlePageChange]);

  const OrderDetailsView = useCallback(() => {
    const uniqueProductCount = selectedProducts.length;

    return (
      <div className={`order-details-container ${nullPriceWarning ? "with-warning" : ""}`}>
        <div className="order-header">
          <div>
            <p className="order-title">Order details</p>
            <div style={{ fontSize: "13px", color: "#666", marginTop: "4px" }}>
              Total: <strong>{formatCurrency(getTotalAmount())}</strong>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div className="item-count-badge">{uniqueProductCount} items</div>
            <button
              type="button"
              onClick={handleCreateInvoice}
              disabled={!canCreateInvoice}
              title={invoiceValidation.message}
              style={{
                padding: "9px 18px",
                border: "none",
                borderRadius: "8px",
                background: canCreateInvoice ? "#28a745" : "#d6d6d6",
                color: canCreateInvoice ? "#fff" : "#777",
                cursor: canCreateInvoice ? "pointer" : "not-allowed",
                fontWeight: 700,
                minWidth: "92px",
              }}
            >
              {isCreatingInvoice ? "Billing..." : "Bill →"}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '6px', padding: '10px 14px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          {["cash", "bank", "cheque", "terminal"].map((method) => (
            <button
              type="button"
              key={method}
              onClick={() => setSelectedPayMethod(method)}
              style={{
                flex: 1,
                padding: '6px 10px',
                borderRadius: '6px',
                border: selectedPayMethod === method ? '2px solid #28a745' : '1px solid #cbd5e1',
                background: selectedPayMethod === method ? '#eefbf2' : '#ffffff',
                color: selectedPayMethod === method ? '#1f8a4c' : '#475569',
                fontWeight: 'bold',
                fontSize: '11px',
                cursor: 'pointer',
                textAlign: 'center'
              }}
            >
              {method === "bank" ? "Card" : method === "terminal" ? "Terminal" : method.charAt(0).toUpperCase() + method.slice(1)}
            </button>
          ))}
        </div>

        {selectedPayMethod === "terminal" && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '6px 14px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#475569' }}>Terminal:</label>
            {terminals.length > 0 ? (
              <select
                value={selectedTerminal?.id || ""}
                onChange={(e) => {
                  const found = terminals.find(t => t.id === e.target.value);
                  setSelectedTerminal(found);
                }}
                style={{
                  flex: 1,
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  background: '#ffffff',
                  color: '#0f172a',
                  outline: 'none'
                }}
              >
                {terminals.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.provider})</option>
                ))}
              </select>
            ) : (
              <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: 'bold' }}>
                No active terminals. Configure in Settings.
              </span>
            )}
          </div>
        )}

        <div className="product-container-wrapper">
          <ProductContainer
            isFullscreen={false}
            products={selectedProducts}
            setProducts={setSelectedProducts}
            onFieldSelect={handleFieldSelect}
            onFieldChange={handleFieldChange}
            selectedField={selectedField}
            onProductDelete={handleProductDelete}
          />
          {nullPriceWarning && (
            <div className="price-warning">
              "{nullPriceWarning.productName}" has no price. Enter Price to add product in the bill.
            </div>
          )}
        </div>

        {!isMobile && (
          <div className="numeric-keypad">
            {[9, 8, 7, 6, 5, 4, 3, 2, 1, "-", 0, "remove", "OK"].map((key, index) => (
              <button
                key={index}
                className={`keypad-button ${key === "OK" ? "ok-button" : ""} ${selectedField.productIndex !== null || (key === "OK" && canCreateInvoice) ? "" : "disabled"}`}
                disabled={key !== "OK" && selectedField.productIndex === null}
                title={key === "OK" && selectedField.productIndex === null ? invoiceValidation.message : undefined}
                onClick={() => handleNumericKeyPress(key)}
              >
                {key === "remove" ? (
                  <img src={removedButton} alt="Remove" className="remove-icon" />
                ) : (
                  key
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }, [selectedProducts, nullPriceWarning, isMobile, selectedField, handleFieldSelect, handleFieldChange, handleProductDelete, handleNumericKeyPress, handleCreateInvoice, canCreateInvoice, invoiceValidation, isCreatingInvoice, formatCurrency, getTotalAmount, selectedPayMethod, terminals, selectedTerminal]);

  const lockElements = (
    <>
      <FullscreenLockSetupModal
        open={isLockSetupOpen}
        password={lockPasswordInput}
        confirmPassword={lockPasswordConfirm}
        error={lockError}
        onPasswordChange={setLockPasswordInput}
        onConfirmPasswordChange={setLockPasswordConfirm}
        onSubmit={handleEnableFullscreenLock}
        onSkip={handleSkipFullscreenLock}
      />

      <FullscreenLockedOverlay
        open={isAppLocked}
        password={unlockPasswordInput}
        error={lockError}
        onPasswordChange={setUnlockPasswordInput}
        onUnlock={handleUnlockFullscreenSession}
        onLogout={handleLockLogout}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="mobile-pos-container">
        <ToastContainer />
        {lockElements}
        {isReceiptOpen && receiptData && (
          <ThermalReceiptModal open={isReceiptOpen} onClose={handleCloseReceipt} data={receiptData} user={user} />
        )}

        <div className="mobile-pos-header">
          <div className="mobile-pos-inner">
            <div className="mobile-total-amount">{formatCurrency(getTotalAmount())}</div>
            <div className="mobile-header-buttons">
              <button className="mobile-cart-icon-button" onClick={() => setShowCartModal(true)}>
                <ShoppingCart size={24} color="black" />
                {selectedProducts.length > 0 && (
                  <span className="cart-badge">{selectedProducts.length}</span>
                )}
              </button>
              <button className="mobile-bill-button" onClick={handleCreateInvoice} disabled={!canCreateInvoice} title={invoiceValidation.message}>{isCreatingInvoice ? "Billing..." : "Bill →"}</button>
            </div>
          </div>
        </div>

        <div className="mobile-product-grid">
          <ProductList
            addProductToContainer={addProductToContainer}
            handleCreateInvoice={handleCreateInvoice}
            onQuantityBlockClick={handleQuantityBlockClick}
            onPriceBlockClick={handlePriceBlockClick}
          />
        </div>

        <div className="mobile-cart-summary">
          <div>
            <span className="item-count-text">{selectedProducts.length} items</span>
          </div>
          <div className="mobile-bottom-buttons">
            <button className="view-transactions-button" onClick={() => setShowTransactionModal(true)}>
              Transactions
            </button>
          </div>
        </div>

        {showCartModal && (
          <div className="mobile-cart-modal-overlay">
            <div className="mobile-cart-modal">
              <div className="cart-modal-header">
                <h3>Cart Items</h3>
                <button className="close-modal-button" onClick={() => setShowCartModal(false)}>×</button>
              </div>
              <div className="cart-modal-content">
                <ProductContainer
                  isFullscreen={false}
                  products={selectedProducts}
                  setProducts={setSelectedProducts}
                  onFieldSelect={handleFieldSelect}
                  onFieldChange={handleFieldChange}
                  selectedField={selectedField}
                  onProductDelete={handleProductDelete}
                />
                {nullPriceWarning && (
                  <div className="price-warning">
                    "{nullPriceWarning.productName}" has no price. Enter Price to add product in the bill.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showTransactionModal && (
          <div className="mobile-cart-modal-overlay">
            <div className="mobile-cart-modal">
              <div className="cart-modal-header">
                <h3>Recent Transactions</h3>
                <button className="close-modal-button" onClick={() => setShowTransactionModal(false)}>×</button>
              </div>
              <div className="cart-modal-content">
                <TransactionTable
                  data={transactionData}
                  loading={transactionLoading}
                  currentPage={transactionPage}
                  totalPages={transactionTotalPages}
                  onPageChange={handlePageChange}
                  onPrint={handleTransactionPrint}
                  compact={true}
                  rowsPerPage={10}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="desktop-pos-container">
      <ToastContainer />
      {lockElements}
      <div className={`desktop-pos-content pos-panel-${layoutPrefs.panelPosition}`}>
        {isReceiptOpen && receiptData && (
          <ThermalReceiptModal open={isReceiptOpen} onClose={handleCloseReceipt} data={receiptData} user={user} />
        )}
        <div className="flip-card-container">
          <div className="flip-card-toggle" onClick={() => setIsFlipped(!isFlipped)}>
            <div className="toggle-text">{isFlipped ? "View Cart" : "View Transactions"}</div>
          </div>
          <ReactCardFlip isFlipped={isFlipped} flipDirection="horizontal">
            <div key="front">
              <OrderDetailsView />
            </div>
            <div key="back">
              <TransactionView />
            </div>
          </ReactCardFlip>
        </div>

        <div className="product-containers">
          <ReactCardFlip isFlipped={isProductFlipped} flipDirection="horizontal">
            <div key="front"><div className="order-details-container"><ProductManagementView /></div></div>
            <div key="back"><div className="order-details-container"><ProductConfigureView /></div></div>
          </ReactCardFlip>
        </div>
      </div>
      <ProductModal
        open={showProductModal && !isAppLocked}
        onClose={() => { setShowProductModal(false); setSelectedBlock(null); }}
        productData={selectedBlock}
        blockIndex={selectedBlock?.index}
        onSave={fetchAllProducts}
        onSaveSection={handleSaveProductSection}
      />

      {showTerminalModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: '#ffffff',
            padding: '30px',
            borderRadius: '12px',
            width: '400px',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {terminalStatus === "CONNECTING" && "🔌"}
              {terminalStatus === "WAITING_FOR_CUSTOMER" && "💳"}
              {terminalStatus === "PROCESSING" && "🔄"}
              {terminalStatus === "SUCCESS" && "✅"}
              {terminalStatus === "FAILED" && "❌"}
            </div>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '20px', color: '#0f172a' }}>
              {terminalStatus === "CONNECTING" && "Connecting to Terminal"}
              {terminalStatus === "WAITING_FOR_CUSTOMER" && "Swipe / Tap / Insert Card"}
              {terminalStatus === "PROCESSING" && "Processing Payment"}
              {terminalStatus === "SUCCESS" && "Payment Successful"}
              {terminalStatus === "FAILED" && "Payment Failed"}
            </h3>
            <p style={{ color: '#475569', fontSize: '14px', margin: '0 0 20px 0', minHeight: '40px', lineHeight: '1.5' }}>
              {terminalMessage}
            </p>
            {terminalStatus === "WAITING_FOR_CUSTOMER" && selectedTerminal && (
              <div style={{ margin: '0 0 20px 0', padding: '10px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <a 
                  href={
                    selectedTerminal.provider === "CLOVER" ? "https://sandbox.dev.clover.com/" :
                    selectedTerminal.provider === "STRIPE" ? "https://stripe.com/docs/terminal/references/testing#simulated-reader" :
                    selectedTerminal.provider === "SQUARE" ? "https://developer.squareup.com/docs/terminal-api/testing" :
                    selectedTerminal.provider === "ADYEN" ? "https://docs.adyen.com/point-of-sale/testing-terminals" :
                    selectedTerminal.provider === "PINE_LABS" ? "https://developer.pinelabs.com/" :
                    selectedTerminal.provider === "PAYTM" ? "https://developer.paytm.com/docs/pos/" : "#"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: '13px', color: '#1d4ed8', fontWeight: 'bold', textDecoration: 'underline' }}
                >
                  👉 Open {selectedTerminal.provider} Simulator / Testing Docs
                </a>
              </div>
            )}
            {terminalStatus === "FAILED" && (
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  onClick={() => handleCreateInvoice()}
                  style={{
                    padding: '8px 16px',
                    background: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Try Again
                </button>
                <button
                  onClick={() => {
                    if (pollingRef.current) clearInterval(pollingRef.current);
                    setShowTerminalModal(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#cbd5e1',
                    color: '#334155',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            {terminalStatus !== "FAILED" && terminalStatus !== "SUCCESS" && (
              <button
                onClick={() => {
                  if (pollingRef.current) clearInterval(pollingRef.current);
                  setShowTerminalModal(false);
                }}
                style={{
                  padding: '8px 16px',
                  background: '#cbd5e1',
                  color: '#334155',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PosData;
